import { Controller } from 'react-hook-form';

import { Box, Button, Switch, Typography, FormControlLabel } from '@mui/material';

import { PasswordField } from 'src/pages/channel/shared';
import { useGoogleAuthPopup } from 'src/pages/channel/channels/Email/hooks/useGoogleAuthPopup';
import { useMicroSoftAuthPopup } from 'src/pages/channel/channels/Email/hooks/useMicroSoftAuthPopup';

import { Field } from 'src/components/hook-form';

export const GoogleFields = ({ selectedChannel, t }: any) => {
  const { loginWithGoogle } = useGoogleAuthPopup({
    Provider: selectedChannel?.config?.settings?.provider ?? '',
  });

  return (
    <>
      <Box sx={{ my: 1, width: '100%' }}>
        <Field.Text
          name="email"
          label={t('EMAIL')}
          InputLabelProps={{ shrink: true }}
          placeholder={t('ENTER_EMAIL_ADDRESS')}
        />
      </Box>
      <Button variant="contained" sx={{ background: '#50a28a', my: 2 }} onClick={loginWithGoogle}>
        {t('CONNECT_WITH_GOOGLE_ACCOUNT')}
      </Button>
    </>
  );
};

export const MicrosoftFields = ({ selectedChannel, t }: any) => {
  const { loginWithMicrosoft } = useMicroSoftAuthPopup({
    Provider: selectedChannel?.config?.settings?.provider ?? '',
    id: selectedChannel.id,
  });

  return (
    <>
      <Box sx={{ my: 1, width: '100%' }}>
        <Field.Text
          name="email"
          label={t('EMAIL')}
          InputLabelProps={{ shrink: true }}
          placeholder={t('ENTER_EMAIL_ADDRESS')}
        />
      </Box>
      <Button
        variant="contained"
        sx={{ background: '#50a28a', my: 2 }}
        onClick={() => loginWithMicrosoft()}
      >
        {t('CONNECT_WITH_MICROSOFT_ACCOUNT')}
      </Button>
    </>
  );
};

export const OtherProviderFields = ({ control, t, watch }: any) => {
  const [watchSmtpEnabled, watchImapEnabled] = watch(['smtpEnabled', 'imapEnabled']);

  return (
    <>
      <Box sx={{ my: 1, width: '100%' }}>
        <Field.Text
          name="email"
          label={t('EMAIL')}
          InputLabelProps={{ shrink: true }}
          placeholder={t('ENTER_EMAIL_ADDRESS')}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, ml: 8 }}>
            <Typography variant="subtitle1">SMTP</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sent your SMTP Details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '60%' }}>
            <FormControlLabel
              control={
                <Controller
                  name="smtpEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      color="primary"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label={
                <Typography variant="subtitle1">
                  Enable SMTP Configuration for this Inbox
                </Typography>
              }
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enabling SMTP will help the user to send email
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '60%', alignSelf: 'end' }}>
          {watchSmtpEnabled && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Field.Text
                name="smtpAddress"
                label={t('SMTP_ADDRESS')}
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_SMTP_ADDRESS')}
              />
              <Field.Text
                name="smtpPort"
                label={t('SMTP_PORT')}
                type="number"
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_SMTP_PORT')}
              />
              <Field.Text
                name="smtpLogin"
                label={t('SMTP_LOGIN')}
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_SMTP_LOGIN')}
              />
              <PasswordField name="smtpPassword" label={t('SMTP_PASSWORD')} />
              <Field.Text
                name="smtpDomain"
                label={t('SMTP_DOMAIN')}
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_SMTP_DOMAIN')}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, ml: 8 }}>
            <Typography variant="subtitle1">IMAP</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sent your IMAP Details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, width: '60%' }}>
            <FormControlLabel
              control={
                <Controller
                  name="imapEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      color="primary"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label={
                <Typography variant="subtitle1">
                  Enable IMAP Configuration for this Inbox
                </Typography>
              }
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enabling IMAP will help the user to receive email
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '60%', alignSelf: 'end' }}>
          {watchImapEnabled && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Field.Text
                name="imapAddress"
                label={t('IMAP_ADDRESS')}
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_IMAP_ADDRESS')}
              />
              <Field.Text
                name="imapPort"
                label={t('IMAP_PORT')}
                type="number"
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_IMAP_PORT')}
              />
              <Field.Text
                name="imapLogin"
                label={t('IMAP_LOGIN')}
                InputLabelProps={{ shrink: true }}
                placeholder={t('ENTER_IMAP_LOGIN')}
              />
              <PasswordField name="imapPassword" label={t('IMAP_PASSWORD')} />
              <FormControlLabel
                control={
                  <Controller
                    name="imapEnableSsl"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        color="primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={<Typography variant="body1">{t('IMAP_ENABLE_SSL')}</Typography>}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
